import { useEffect, useRef, useState } from 'react';
import ArchitectureCanvas from '../components/architecture/ArchitectureCanvas';
import ArchitecturePalette from '../components/architecture/ArchitecturePalette';
import ArchitecturePropertiesPanel from '../components/architecture/ArchitecturePropertiesPanel';
import ArchitectureToolbar from '../components/architecture/ArchitectureToolbar';
import ResizeHandle from '../components/layout/ResizeHandle';
import { useArchitectureKeyboardShortcuts } from '../hooks/useArchitectureKeyboardShortcuts';
import { navigateToDashboard } from '../App';
import { useDiagramViewStore } from '../store/useDiagramViewStore';
import { useWorkspaceStore } from '../store/useWorkspaceStore';
import { useArchitectureProjectsStore } from '../store/useArchitectureProjectsStore';
import { useArchitectureStore } from '../store/useArchitectureStore';

const LEFT_MIN = 200;
const LEFT_MAX = 480;
const RIGHT_MIN = 280;
const RIGHT_MAX = 680;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export default function ArchitectureEditor({ projectId }: { projectId: string }) {
  const hydrated = useArchitectureProjectsStore((s) => s.hydrated);
  const hydrate = useArchitectureProjectsStore((s) => s.hydrate);
  const getProject = useArchitectureProjectsStore((s) => s.get);
  const loadProject = useArchitectureStore((s) => s.loadProject);
  const dirty = useArchitectureStore((s) => s.dirty);
  const save = useArchitectureStore((s) => s.save);
  const loadedProjectId = useArchitectureStore((s) => s.projectId);

  // Members without the "edit diagrams" permission get a view-only editor.
  const canEdit = useWorkspaceStore((s) => s.canEditDiagram('architecture', projectId));
  const setReadOnly = useDiagramViewStore((s) => s.setReadOnly);
  useEffect(() => {
    setReadOnly(!canEdit);
    return () => setReadOnly(false);
  }, [canEdit, setReadOnly]);

  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [leftWidth, setLeftWidth] = useState(260);
  const [rightWidth, setRightWidth] = useState(320);
  const panelWide = useDiagramViewStore((s) => s.panelWide);
  const docsRequested = useDiagramViewStore((s) => s.docsRequested);

  // Asking for the explanation (About button, "Go to explanation" menu) opens the side panel if it was closed.
  useEffect(() => {
    if (docsRequested) setRightOpen(true);
  }, [docsRequested]);

  // The About tab's "Wider" button asks for a roomier panel while reading a long explanation.
  useEffect(() => {
    setRightWidth(panelWide ? RIGHT_MAX : 320);
  }, [panelWide]);
  useEffect(() => () => useDiagramViewStore.setState({ panelWide: false, focusedColumn: null, tourOpen: false, tourFocus: null }), []);
  const diagramRef = useRef<HTMLDivElement>(null);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    if (!hydrated) return;
    if (loadedProjectId === projectId) return;
    const project = getProject(projectId);
    if (!project) {
      navigateToDashboard();
      return;
    }
    loadProject(project);
  }, [hydrated, projectId, loadedProjectId, getProject, loadProject]);

  useEffect(() => {
    if (!dirty || !canEdit) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => save(), 800);
    return () => clearTimeout(autosaveTimer.current);
  }, [dirty, save, canEdit]);

  useArchitectureKeyboardShortcuts();

  if (loadedProjectId !== projectId) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas text-sm text-gray-400 dark:bg-gray-950 dark:text-gray-500">
        Loading project...
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <ArchitectureToolbar
        diagramRef={diagramRef}
        leftOpen={leftOpen}
        onToggleLeft={() => setLeftOpen((v) => !v)}
        rightOpen={rightOpen}
        onToggleRight={() => setRightOpen((v) => !v)}
      />

      <div className="flex flex-1 overflow-hidden">
        {leftOpen && (
          <>
            <fieldset disabled={!canEdit} className="contents">
              <ArchitecturePalette width={leftWidth} />
            </fieldset>
            <ResizeHandle onResize={(dx) => setLeftWidth((w) => clamp(w + dx, LEFT_MIN, LEFT_MAX))} />
          </>
        )}

        <div className="relative flex-1 overflow-hidden">
          <ArchitectureCanvas containerRef={diagramRef} />
        </div>

        {rightOpen && (
          <>
            <ResizeHandle onResize={(dx) => setRightWidth((w) => clamp(w - dx, RIGHT_MIN, RIGHT_MAX))} />
            <fieldset disabled={!canEdit} className="contents">
              <ArchitecturePropertiesPanel width={rightWidth} />
            </fieldset>
          </>
        )}
      </div>
    </div>
  );
}
